import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import getConfig from 'next/config';

import { setErrorAction } from 'redux/modules/video/error';

import BrowserDetection from 'lib/BrowserDetection';

import Loader from 'components/IconLoader';
import PlaymakerPlayer from 'components/PlaymakerPlayer';
import Slate, { ERROR_TYPES } from 'components/TVE/Slate';

import {
  getAdLevelForPID,
  MSNBC_TVE_PID,
  playmakerConfiguration,
} from 'lib/playmaker';

import placeholderVideo from 'assets/videos/msnbc-placeholder.mp4';

const {
  publicRuntimeConfig,
} = getConfig();

const block = 'tve__player';

const mapStateToProps = ({ shared, tve, video }) => ({
  authenticated: tve.authenticated,
  authorization: tve.authorization,
  hasTempPass: tve.hasTempPass,
  selectedProvider: tve.selected,
  tempPassProviderID: tve.tempPassProviderID,
  videoError: video.error,
  vertical: shared.vertical,
});

const mapActionToProps = (dispatch) => ({
  setError: (payload) => dispatch(setErrorAction(payload)),
});

class Player extends React.Component {
  static propTypes = {
    authenticated: PropTypes.bool,
    authorization: PropTypes.string,
    autoplayMuted: PropTypes.bool,
    bypassAuth: PropTypes.bool,
    externalControlbar: PropTypes.oneOf([0, 1]),
    hasTempPass: PropTypes.bool,
    selectedProvider: PropTypes.shape({
      freewheelHash: PropTypes.string,
      id: PropTypes.string,
    }),
    setError: PropTypes.func.isRequired,
    tempPassProviderID: PropTypes.string,
    vertical: PropTypes.string.isRequired,
    videoError: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
  };

  static defaultProps = {
    authenticated: false,
    authorization: null,
    autoplayMuted: false,
    bypassAuth: false,
    externalControlbar: 0,
    hasTempPass: null,
    selectedProvider: {
      freewheelHash: null,
      id: null,
    },
    tempPassProviderID: null,
    videoError: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      placeholder: false,
    };
  }

  componentDidMount() {
    // Does not work in IE, throw browser error
    if (BrowserDetection.isIE() && BrowserDetection.version() <= 11) {
      const { setError } = this.props;
      setError(ERROR_TYPES.BROWSER);
    }

    // Prevent giant placeholder video from displaying unstyled until mounted
    this.setState({ placeholder: true });
  }

  renderPlaceholder = () => (
    <div className={`${block}__slate absolute absolute-fill`}>
      <div className={`${block}__slate__background absolute absolute-fill`} />
      <video
        width="0px"
        height="0px"
        autoPlay
        loop
        muted
        className={`${block}__slate__video`}
      >
        <source src={placeholderVideo} type="video/mp4" />
      </video>
    </div>
  );

  renderError = () => (
    <div className={`${block}__error absolute absolute-fill`}>
      <Slate />
    </div>
  );

  renderLoader = () => (
    <div className={`${block}__loader absolute absolute-fill`}>
      <Loader
        className="relative"
        brand="news"
        animateIn
        animateOut
      />
    </div>
  );

  renderPlayer = () => {
    const {
      authorization,
      autoplayMuted,
      externalControlbar,
      hasTempPass,
      selectedProvider,
      tempPassProviderID,
      vertical,
    } = this.props;

    const leapConfigUrl = get(playmakerConfiguration, `${vertical}.leapConfigUrl`, null);

    let providerId;
    if (hasTempPass) {
      providerId = tempPassProviderID;
    } else {
      providerId = get(selectedProvider, 'freewheelHash', null);
    }

    return (
      <div className={`${block}__player absolute absolute-fill`}>
        <PlaymakerPlayer
          adLevel={getAdLevelForPID(MSNBC_TVE_PID, publicRuntimeConfig)}
          afid={null}
          authorization={authorization}
          autoplay
          autoplayMuted={autoplayMuted}
          cdnTokenEndpoint={null}
          csid={null}
          externalControlbar={externalControlbar}
          isLive
          leapConfigUrl={leapConfigUrl}
          metr={null}
          parseScte={1}
          pid={parseInt(MSNBC_TVE_PID, 10)}
          poster={null}
          providerId={providerId}
          resource={null}
          sfid={null}
          vertical={vertical}
        />
      </div>
    );
  }

  render() {
    const {
      authenticated, authorization, bypassAuth, videoError,
    } = this.props;
    const { placeholder } = this.state;

    return (
      <div className={`${block} absolute absolute-fill`}>
        {videoError !== false && (
          this.renderError()
        )}
        {bypassAuth ? (
          this.renderPlayer()
        ) : (
          <>
            {authenticated && !authorization && (
              this.renderLoader()
            )}
            {authenticated && authorization && (
              this.renderPlayer()
            )}
          </>
        )}
        {placeholder && (
          this.renderPlaceholder()
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapActionToProps)(Player);
